import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Switch, FormControlLabel
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import ImpresionLetraCard from '../../components/configuraciones/ImpresionLetraCard';
import { useSnackbar } from 'notistack';
import sucVentaService, {
  SucVenta,
  SucVentaCatalogoImpresion,
  SucVentaImpresionLetra,
} from '../../services/sucVentaService';
import { IMPRESION_MOTOR, mergeImpresionLetra, parseImpresionDesdeApi } from '../../constants/sucVentaImpresion';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';
import tipoFormularioService, { TipoFormulario, TIPO_FORMULARIO_MAX_PAGE_SIZE } from '../../services/tipoFormularioService';
import wsfeConfigService, { WsfeConfigItem } from '../../services/wsfeConfigService';
const defaultImpresionLetra = (): SucVentaImpresionLetra =>
  mergeImpresionLetra(undefined, {}, null);

type PlantiKey = 'plantiA' | 'plantiB' | 'plantiC' | 'plantiN' | 'plantiComun';

const leerCampoPlanti = (suc: SucVenta, plantiKey: PlantiKey): string =>
  (suc[plantiKey] ?? '').trim();

const normalizarImpresionLetra = (
  impRaw: unknown,
  planti?: string | null,
): SucVentaImpresionLetra =>
  mergeImpresionLetra(undefined, parseImpresionDesdeApi(impRaw), planti);

const normalizarImpresionSucVenta = (suc: SucVenta): SucVenta => ({
  ...suc,
  impresionA: normalizarImpresionLetra(suc.impresionA, leerCampoPlanti(suc, 'plantiA')),
  impresionB: normalizarImpresionLetra(suc.impresionB, leerCampoPlanti(suc, 'plantiB')),
  impresionC: normalizarImpresionLetra(suc.impresionC, leerCampoPlanti(suc, 'plantiC')),
  impresionN: normalizarImpresionLetra(suc.impresionN, leerCampoPlanti(suc, 'plantiN')),
  impresionComun: normalizarImpresionLetra(suc.impresionComun, leerCampoPlanti(suc, 'plantiComun')),
});

const IMPRESION_LETRAS = [
  { titulo: 'Letra A', impresionKey: 'impresionA' as const, impKey: 'impA' as const, plantiKey: 'plantiA' as const },
  { titulo: 'Letra B', impresionKey: 'impresionB' as const, impKey: 'impB' as const, plantiKey: 'plantiB' as const },
  { titulo: 'Letra C', impresionKey: 'impresionC' as const, impKey: 'impC' as const, plantiKey: 'plantiC' as const },
  { titulo: 'Letra N', impresionKey: 'impresionN' as const, impKey: 'impN' as const, plantiKey: 'plantiN' as const },
  { titulo: 'Común / Letra X', impresionKey: 'impresionComun' as const, impKey: 'impComun' as const, plantiKey: 'plantiComun' as const },
];

const defaultForm: SucVenta = {
  nroSuc: 0,
  nroTipoFormulario: '',
  formuA: 0,
  formuB: 0,
  formuC: 0,
  formuN: 0,
  numPropio: 'N',
  nroTipoFormularioNumerador: '',
  copias: 1,
  renglones: 10,
  plantiA: '',
  plantiB: '',
  plantiC: '',
  plantiN: '',
  plantiComun: '',
  impA: '',
  impB: '',
  impC: '',
  impN: '',
  impComun: '',
  impresionA: defaultImpresionLetra(),
  impresionB: defaultImpresionLetra(),
  impresionC: defaultImpresionLetra(),
  impresionN: defaultImpresionLetra(),
  impresionComun: defaultImpresionLetra(),
  vpreviaA: 'N',
  vpreviaB: 'N',
  vpreviaC: 'N',
  vpreviaN: 'N',
  vpreviaComun: 'N',
  imprime: 'N',
  liquidaComision: 'N',
  afectaLimCred: 'N',
  actualizaLimCred: 'N',
  tipoImpresora: '',
  usaCodigoBarra: 0,
  tipoRenglones: '',
  idWsfe: '',
  generaRemito: 'N',
  nroSucRemito: 0,
  soloAdvertencia: 0,
  usaLetraM: 0,
  idWsfecred: '',
  passEmision: '',
  passAnulacion: '',
  generaOcarga: 'N',
  nroSucOcarga: 0
};

const TIPOS_IMPRESORA: { label: string; value: string }[] = [
  { label: 'Matriz de Puntos', value: 'M' },
  { label: 'Chorro a Tinta', value: 'T' },
  { label: 'Laser', value: 'L' },
  { label: 'Ticketera EPSON', value: 'TE' },
  { label: 'Fiscal Epson', value: 'FE' },
  { label: 'Fiscal Epson 2', value: 'FE2' },
  { label: 'Fiscal Epson 3', value: 'FE3' },
  { label: 'Fiscal Hasar', value: 'FH' },
  { label: 'Fiscal Hasar UM', value: 'FHUM' },
  { label: 'Fiscal Buffer', value: 'FBUF' },
  { label: 'Web Service FE', value: 'WSFE' },
];

const SucVentaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroSuc, nroTipoFormulario } = useParams();
  const [formData, setFormData] = useState<SucVenta>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [tipoFormularios, setTipoFormularios] = useState<TipoFormulario[]>([]);
  const [wsfeConfigs, setWsfeConfigs] = useState<WsfeConfigItem[]>([]);
  const [catalogoImpresion, setCatalogoImpresion] = useState<SucVentaCatalogoImpresion>({
    motores: [],
    variantesPlantilla: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(false);

  const isNew = !nroSuc || !nroTipoFormulario || nroSuc === 'nuevo' || nroTipoFormulario === 'nuevo';

  const loadSucVenta = useCallback(async () => {
    if (!nroSuc || !nroTipoFormulario || nroSuc === 'nuevo' || nroTipoFormulario === 'nuevo') return;
    try {
      setLoading(true);
      const sucVenta = await sucVentaService.getSucVenta(Number(nroSuc), nroTipoFormulario);
      setFormData(normalizarImpresionSucVenta(sucVenta));
    } catch (error) {
      enqueueSnackbar('Error al cargar la asignación', { variant: 'error' });
      navigate('/configuraciones/sucventa');
    } finally {
      setLoading(false);
    }
  }, [nroSuc, nroTipoFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo') loadSucVenta();
  }, [nroSuc, nroTipoFormulario, loadSucVenta]);

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [sucRes, tipRes, wsfeRes, catalogoRes] = await Promise.all([
          sucursalesVentaService.getSucursalesVenta({ pageNumber: 1, pageSize: 20 }),
          tipoFormularioService.getTipoFormularios({ pageNumber: 1, pageSize: TIPO_FORMULARIO_MAX_PAGE_SIZE }),
          wsfeConfigService.getWsfeConfigs(),
          sucVentaService.getCatalogoImpresion(),
        ]);
        setSucursales(sucRes.items || []);
        setTipoFormularios(tipRes.items || []);
        setWsfeConfigs(Array.isArray(wsfeRes) ? wsfeRes : []);
        setCatalogoImpresion(catalogoRes);
      } catch (err) {
        console.error('Error al cargar opciones:', err);
        enqueueSnackbar('Error al cargar sucursales y tipos de formulario', { variant: 'error' });
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [enqueueSnackbar]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (['nroSuc', 'formuA', 'formuB', 'formuC', 'formuN', 'copias', 'renglones', 'usaCodigoBarra', 'nroSucRemito', 'soloAdvertencia', 'usaLetraM', 'nroSucOcarga'].includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleImpresionChange = (
    impresionKey: (typeof IMPRESION_LETRAS)[number]['impresionKey'],
    plantiKey: (typeof IMPRESION_LETRAS)[number]['plantiKey'],
    patch: Partial<SucVentaImpresionLetra>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [impresionKey]: mergeImpresionLetra(
        prev[impresionKey],
        patch,
        leerCampoPlanti(prev, plantiKey),
      ),
    }));
  };

  const validarImpresion = (): string | null => {
    for (const letra of IMPRESION_LETRAS) {
      const imp = formData[letra.impresionKey];
      if (imp?.motor === IMPRESION_MOTOR.CRYSTAL && !imp.rutaCrystal?.trim()) {
        return `${letra.titulo}: ingrese la ruta del archivo .rpt para Crystal.`;
      }
    }
    return null;
  };

  const sucursalOptions = useMemo(
    () => sucursales.map((s) => ({ value: s.nroSuc, label: `${s.nroSuc} - ${s.nombreSucursal}` })),
    [sucursales],
  );

  const tipoFormularioOptions = useMemo(
    () =>
      tipoFormularios.map((tf) => ({
        value: String(tf.nroTipoFormulario).trim(),
        label: `${tf.nroTipoFormulario} - ${tf.descripcion}`,
      })),
    [tipoFormularios],
  );

  const tipoImpresoraOptions = useMemo(() => {
    const options = TIPOS_IMPRESORA.map((tipo) => ({ value: tipo.value, label: tipo.label }));
    const actual = (formData.tipoImpresora || '').trim();
    if (actual && !TIPOS_IMPRESORA.some((t) => t.value === actual)) {
      options.push({ value: actual, label: `${actual} (valor actual)` });
    }
    return options;
  }, [formData.tipoImpresora]);

  const wsfeConfigOptions = useMemo(
    () =>
      wsfeConfigs.map((c) => ({
        value: c.idwsfe,
        label: `${c.idwsfe}${c.descripcion ? ` - ${c.descripcion}` : ''}`,
      })),
    [wsfeConfigs],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (isNew) {
        if (!formData.nroSuc) {
          enqueueSnackbar('Seleccione una sucursal de venta', { variant: 'warning' });
          return;
        }
        if (!formData.nroTipoFormulario?.trim()) {
          enqueueSnackbar('Seleccione un tipo de formulario', { variant: 'warning' });
          return;
        }
      }

      const errorImpresion = validarImpresion();
      if (errorImpresion) {
        enqueueSnackbar(errorImpresion, { variant: 'warning' });
        setCurrentTab(2);
        return;
      }

      const payload: SucVenta = {
        ...formData,
        numPropio: (formData.numPropio || 'N').toUpperCase() === 'S' ? 'S' : 'N',
        impresionA: formData.impresionA,
        impresionB: formData.impresionB,
        impresionC: formData.impresionC,
        impresionN: formData.impresionN,
        impresionComun: formData.impresionComun,
      };
      if (nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo') {
        await sucVentaService.updateSucVenta(Number(nroSuc), nroTipoFormulario, payload);
      } else {
        await sucVentaService.createSucVenta(payload);
      }

      enqueueSnackbar('Asignación guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/sucventa');
    } catch (error: any) {
      console.error('Error al guardar asignación:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la asignación', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo' ? 'Editar Asignación' : 'Nueva Asignación'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Numeración" />
            <Tab label="Impresión" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {isNew ? (
                    <SearchableSelect<number>
                      label="Sucursal de Venta"
                      value={formData.nroSuc || null}
                      onChange={(val) => setFormData((prev) => ({ ...prev, nroSuc: val ?? 0 }))}
                      options={sucursalOptions}
                      required
                      allowEmpty
                      emptyOptionLabel="Seleccionar sucursal"
                      loading={loadingOptions}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : (
                    <TextField 
                      name="nroSuc" 
                      label="Sucursal de Venta" 
                      value={`${formData.nroSuc}${formData.nombreSucursal ? ` - ${formData.nombreSucursal}` : ''}`}
                      fullWidth 
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isNew ? (
                    <SearchableSelect
                      label="Tipo de Formulario"
                      value={formData.nroTipoFormulario || null}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, nroTipoFormulario: val?.trim() ?? '' }))
                      }
                      options={tipoFormularioOptions}
                      required
                      allowEmpty
                      emptyOptionLabel="Seleccionar tipo de formulario"
                      loading={loadingOptions}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : (
                    <TextField 
                      name="nroTipoFormulario" 
                      label="Tipo de Formulario" 
                      value={`${formData.nroTipoFormulario || ''}${formData.tipoFormulario?.descripcion ? ` - ${formData.tipoFormulario.descripcion.trim()}` : ''}`}
                      fullWidth 
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="copias" 
                    label="Copias" 
                    type="number"
                    value={formData.copias || 1} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="renglones" 
                    label="Renglones" 
                    type="number"
                    value={formData.renglones || 10} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.imprime === 'S'} onChange={handleSwitchChange('imprime')} />}
                    label="Imprime"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.liquidaComision === 'S'} onChange={handleSwitchChange('liquidaComision')} />}
                    label="Liquida Comisión"
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuA" 
                    label="Formulario A" 
                    type="number"
                    value={formData.formuA || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuB" 
                    label="Formulario B" 
                    type="number"
                    value={formData.formuB || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuC" 
                    label="Formulario C" 
                    type="number"
                    value={formData.formuC || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuN" 
                    label="Formulario N" 
                    type="number"
                    value={formData.formuN || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={(formData.numPropio || 'N') === 'S'} 
                        onChange={handleSwitchChange('numPropio')} 
                      />
                    }
                    label="Impresión propia"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroTipoFormularioNumerador" 
                    label="Tipo Form. Numerador" 
                    value={formData.nroTipoFormularioNumerador || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Box role="tabpanel" hidden={currentTab !== 2}>
              <Grid container spacing={3}>
                {IMPRESION_LETRAS.map((letra) => (
                  <ImpresionLetraCard
                    key={letra.impresionKey}
                    titulo={letra.titulo}
                    impresion={formData[letra.impresionKey] ?? defaultImpresionLetra()}
                    impresora={formData[letra.impKey] ?? ''}
                    catalogo={catalogoImpresion}
                    onImpresionChange={(patch) => handleImpresionChange(letra.impresionKey, letra.plantiKey, patch)}
                    onImpresoraChange={(value) =>
                      setFormData((prev) => ({ ...prev, [letra.impKey]: value }))
                    }
                  />
                ))}
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    label="Tipo Impresora"
                    value={(formData.tipoImpresora || '').trim() || null}
                    onChange={(val) => setFormData((prev) => ({ ...prev, tipoImpresora: val ?? '' }))}
                    options={tipoImpresoraOptions}
                    allowEmpty
                    emptyOptionLabel="Seleccionar..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.usaCodigoBarra === 1} onChange={(e) => setFormData(prev => ({ ...prev, usaCodigoBarra: e.target.checked ? 1 : 0 }))} />}
                    label="Usa Código de Barras"
                  />
                </Grid>
              </Grid>
            </Box>

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.afectaLimCred === 'S'} onChange={handleSwitchChange('afectaLimCred')} />}
                    label="Afecta Límite de Crédito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.actualizaLimCred === 'S'} onChange={handleSwitchChange('actualizaLimCred')} />}
                    label="Actualiza Límite de Crédito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.generaRemito === 'S'} onChange={handleSwitchChange('generaRemito')} />}
                    label="Genera Remito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect<number>
                    label="N° Suc. Remito"
                    value={
                      formData.nroSucRemito != null && formData.nroSucRemito !== 0
                        ? formData.nroSucRemito
                        : null
                    }
                    onChange={(val) => setFormData((prev) => ({ ...prev, nroSucRemito: val ?? 0 }))}
                    options={sucursalOptions}
                    allowEmpty
                    emptyOptionLabel="Ninguna"
                    loading={loadingOptions}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.generaOcarga === 'S'} onChange={handleSwitchChange('generaOcarga')} />}
                    label="Genera Orden de Carga"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect<number>
                    label="N° Suc. O. Carga"
                    value={
                      formData.nroSucOcarga != null && formData.nroSucOcarga !== 0
                        ? formData.nroSucOcarga
                        : null
                    }
                    onChange={(val) => setFormData((prev) => ({ ...prev, nroSucOcarga: val ?? 0 }))}
                    options={sucursalOptions}
                    allowEmpty
                    emptyOptionLabel="Ninguna"
                    loading={loadingOptions}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    label="ID WSFE"
                    value={(formData.idWsfe || '').trim() || null}
                    onChange={(val) => setFormData((prev) => ({ ...prev, idWsfe: val?.trim() ?? '' }))}
                    options={wsfeConfigOptions}
                    allowEmpty
                    emptyOptionLabel="Ninguno"
                    loading={loadingOptions}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    label="ID WSFE Cred"
                    value={(formData.idWsfecred || '').trim() || null}
                    onChange={(val) => setFormData((prev) => ({ ...prev, idWsfecred: val?.trim() ?? '' }))}
                    options={wsfeConfigOptions}
                    allowEmpty
                    emptyOptionLabel="Ninguno"
                    loading={loadingOptions}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/sucventa')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucVentaForm;
